/**
 * LiveMonitor — admin-only page that shows every active till in real time.
 *
 * The page polls /api/live/tills every 3s and lays them out as a grid of
 * cards. Click a card → an inline detail panel slides in with the full
 * cart contents, last action and a "Send message" form. Messages pop up on
 * the cashier's POS as a toast within 5s.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Activity,
  RefreshCw,
  Send,
  ShoppingCart,
  User,
  Clock,
  Loader2,
  MessageSquare,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;

const fmtR = (n) =>
  `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const secondsAgo = (iso) => {
  if (!iso) return null;
  try {
    return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  } catch {
    return null;
  }
};

export default function LiveMonitor() {
  const [tills, setTills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  // Inline quick-message panel (on the main Live Monitor screen, not the modal).
  const [quickTarget, setQuickTarget] = useState("");
  const [quickDraft, setQuickDraft] = useState("");
  const [quickSending, setQuickSending] = useState(false);

  // Local clock tick so "X seconds ago" labels stay fresh even if no new
  // heartbeat data arrived this cycle.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchTills = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/live/tills`, { timeout: 6000 });
      setTills(res.data?.tills || []);
      setFetchedAt(res.data?.fetched_at || new Date().toISOString());
    } catch (err) {
      console.error("LiveMonitor fetchTills failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/api/live/tills/${sessionId}`, { timeout: 6000 });
      setSelectedDetail(res.data || null);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to load till.";
      toast.error(detail);
      setSelectedSessionId(null);
      setSelectedDetail(null);
    }
  }, []);

  useEffect(() => {
    fetchTills();
    const t = setInterval(fetchTills, POLL_MS);
    return () => clearInterval(t);
  }, [fetchTills]);

  // Refresh selected detail more often than the list to feel "live".
  useEffect(() => {
    if (!selectedSessionId) return undefined;
    fetchDetail(selectedSessionId);
    const t = setInterval(() => fetchDetail(selectedSessionId), POLL_MS);
    return () => clearInterval(t);
  }, [selectedSessionId, fetchDetail]);

  const sortedTills = useMemo(() => {
    return [...tills].sort((a, b) =>
      (a.user_name || "").localeCompare(b.user_name || ""),
    );
  }, [tills]);

  // Keep the quick-message target sensible: if there's exactly one active till,
  // auto-select it. If the previously selected till disappeared, clear it.
  useEffect(() => {
    if (sortedTills.length === 1) {
      setQuickTarget(sortedTills[0].session_id);
      return;
    }
    if (quickTarget && !sortedTills.some((t) => t.session_id === quickTarget)) {
      setQuickTarget("");
    }
  }, [sortedTills, quickTarget]);

  const handleQuickSend = async () => {
    if (!quickTarget) {
      toast.warning("Select a cashier first.");
      return;
    }
    if (!quickDraft.trim()) {
      toast.warning("Type a message first.");
      return;
    }
    setQuickSending(true);
    try {
      await axios.post(`${API}/api/live/tills/${quickTarget}/message`, {
        message: quickDraft.trim(),
      });
      const tgt = sortedTills.find((t) => t.session_id === quickTarget);
      toast.success(`Message sent to ${tgt?.user_name || "cashier"}.`);
      setQuickDraft("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message.");
    } finally {
      setQuickSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedSessionId || !messageDraft.trim()) {
      toast.warning("Type a message first.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/live/tills/${selectedSessionId}/message`, {
        message: messageDraft.trim(),
      });
      toast.success(`Message sent to ${selectedDetail?.user_name || "till"}.`);
      setMessageDraft("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="live-monitor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-blue-600" />
            Live POS Monitor
          </h1>
          <p className="text-sm text-gray-500">
            Active tills updated every {POLL_MS / 1000}s. Cashier's cart and last action shown in real time.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchTills}
          disabled={loading}
          data-testid="live-monitor-refresh"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Inline quick-message panel — send straight from this screen without
          opening the per-till modal. */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 mb-2 text-blue-900 font-medium text-sm">
            <MessageSquare className="h-4 w-4" />
            Quick message to cashier
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="sm:w-56">
              <Select
                value={quickTarget}
                onValueChange={setQuickTarget}
                disabled={sortedTills.length === 0}
              >
                <SelectTrigger data-testid="quick-msg-cashier-select">
                  <SelectValue
                    placeholder={
                      sortedTills.length === 0
                        ? "No active tills"
                        : "Choose cashier…"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {sortedTills.map((t) => (
                    <SelectItem key={t.session_id} value={t.session_id}>
                      {t.user_name || "Unknown"}
                      {t.role ? ` · ${t.role}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input
              value={quickDraft}
              onChange={(e) => setQuickDraft(e.target.value)}
              placeholder="Type a message — pops up on the till within ~5s"
              maxLength={500}
              disabled={!quickTarget}
              data-testid="quick-msg-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleQuickSend();
                }
              }}
              className="flex-1"
            />
            <Button
              onClick={handleQuickSend}
              disabled={quickSending || !quickTarget || !quickDraft.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="quick-msg-send-btn"
            >
              {quickSending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              Send
            </Button>
          </div>
          <p className="text-[11px] text-blue-700/70 mt-1">
            Tip: with one active till it auto-selects. Press <kbd>Enter</kbd> to send.
          </p>
        </CardContent>
      </Card>

      {loading && tills.length === 0 ? (
        <div className="text-sm text-gray-400 py-12 text-center">Loading active tills…</div>
      ) : sortedTills.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-gray-500">
            No tills are active right now. As soon as a cashier opens the POS Sales page their till will appear here.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sortedTills.map((till) => {
            const stale = secondsAgo(till.last_heartbeat) ?? 0;
            const isStale = stale > 10;
            return (
              <Card
                key={till.session_id}
                className={`cursor-pointer hover:shadow-md transition border-l-4 ${
                  isStale ? "border-amber-400" : "border-emerald-500"
                }`}
                onClick={() => setSelectedSessionId(till.session_id)}
                data-testid={`live-till-card-${till.session_id}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      {till.user_name || "Unknown user"}
                    </span>
                    <span className="text-xs font-normal text-gray-400">
                      {till.role || "user"}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 flex items-center gap-1">
                      <ShoppingCart className="h-3.5 w-3.5" />
                      Cart
                    </span>
                    <span className="font-medium">
                      {till.cart_count || 0} line{till.cart_count === 1 ? "" : "s"} ·{" "}
                      {fmtR(till.cart_total)}
                    </span>
                  </div>
                  {till.customer_name && (
                    <div className="text-xs text-blue-700">
                      Customer: <span className="font-medium">{till.customer_name}</span>
                    </div>
                  )}
                  {till.last_action && (
                    <div className="text-xs text-gray-600 italic">{till.last_action}</div>
                  )}
                  <div className="text-[11px] text-gray-400 flex items-center gap-1 pt-1">
                    <Clock className="h-3 w-3" />
                    Heartbeat {stale}s ago
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {fetchedAt && (
        <div className="text-[11px] text-gray-400">
          Last fetched: {new Date(fetchedAt).toLocaleString()} · {tills.length} active till{tills.length === 1 ? "" : "s"}
        </div>
      )}

      <Dialog
        open={Boolean(selectedSessionId)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedSessionId(null);
            setSelectedDetail(null);
            setMessageDraft("");
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="live-till-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              {selectedDetail?.user_name || "Till"}
            </DialogTitle>
            <DialogDescription>
              Started {selectedDetail?.started_at ? new Date(selectedDetail.started_at).toLocaleString() : "—"}
              {selectedDetail?.last_heartbeat && (
                <> · Heartbeat {secondsAgo(selectedDetail.last_heartbeat)}s ago</>
              )}
            </DialogDescription>
            {/* nowTick keeps the heartbeat label re-rendering even when no
                new fetch has come in this second. */}
            <span className="hidden">{nowTick}</span>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 space-y-3">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-md bg-gray-50 p-3">
                <div className="text-xs text-gray-500">Cart total</div>
                <div className="font-semibold">{fmtR(selectedDetail?.cart_total)}</div>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <div className="text-xs text-gray-500">Lines</div>
                <div className="font-semibold">{selectedDetail?.cart_count || 0}</div>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <div className="text-xs text-gray-500">Customer</div>
                <div className="font-semibold">{selectedDetail?.customer_name || "—"}</div>
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 px-3 py-2">
                Cart contents
              </div>
              {selectedDetail?.cart_items?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 bg-gray-50/60 border-t">
                    <tr>
                      <th className="px-3 py-1 text-left">Item</th>
                      <th className="px-3 py-1 text-right">Qty</th>
                      <th className="px-3 py-1 text-right">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDetail.cart_items.map((line, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-1">{line.name}</td>
                        <td className="px-3 py-1 text-right tabular-nums">{line.qty}</td>
                        <td className="px-3 py-1 text-right tabular-nums">{fmtR(line.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-xs text-gray-400 italic px-3 py-3">Cart is empty.</div>
              )}
            </div>

            {selectedDetail?.last_action && (
              <div className="text-xs text-gray-500 italic">
                Last action: {selectedDetail.last_action}
              </div>
            )}

            <div className="border rounded-md p-3 bg-blue-50/50">
              <label className="text-xs font-semibold text-blue-900 flex items-center gap-1">
                <Send className="h-3.5 w-3.5" />
                Send a quick message to this cashier
              </label>
              <p className="text-[11px] text-blue-700/80 mb-2">
                Pops up as a toast on the cashier's POS within ~5s.
              </p>
              <div className="flex gap-2">
                <Input
                  value={messageDraft}
                  onChange={(e) => setMessageDraft(e.target.value)}
                  placeholder="e.g. Please call me before completing this sale."
                  maxLength={500}
                  data-testid="live-monitor-message-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || !messageDraft.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="live-monitor-send-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
